import { CartProductEntry } from '../../common/cart-management/cartConfig'
import { AutoDeliveryConfig, CategoryNameMapping } from '../../common/config'
import { ProductDataVariant } from '../../../../services/product/product-data'
import { AutoDeliveryPeriodicityCategory } from '../../../../services/auto-delivery/types'

export interface ValidationMessageParams {
  stickyItemsCount: number
  isAutoDeliveryCartOrderType: boolean
  autoDeliveryMaxCart: number
  autoDeliveryMinCart?: number
  autoDeliveryConfig: AutoDeliveryConfig
  categoryQuantities: Map<string, number>
  periodicityCategories: AutoDeliveryPeriodicityCategory[]
  categoryNames: CategoryNameMapping[]
}

export interface ValidationMessageResult {
  message: string
  continueDisabled: boolean
  disabledCategoryIds: string[]
}

/**
 * Returns the effective category code for a product variant.
 * Prefers `productCategoryCode` (new CBX field) and falls back to `categoryIdentifier`.
 */
export const getProductCategoryCode = (
  variant?: ProductDataVariant
): string | undefined => {
  return variant?.productCategoryCode ?? variant?.categoryIdentifier
}

/**
 * Returns the effective category key from the AD config periodicity category.
 * When categoryQuantities is provided, checks which config field actually has a
 * matching entry in the map — this handles the case where products only have
 * categoryIdentifier (mapped to categoryId in config) but the config also has
 * platformBrandCode defined.
 *
 * Priority: platformBrandCode > categoryId.
 *
 * The product's `productCategoryCode` is compared against the config's
 * `platformBrandCode` (DCXWAM-40952).
 */
export const getConfigCategoryKey = (
  category: AutoDeliveryPeriodicityCategory,
  categoryQuantities?: Map<string, number>
): string | undefined => {
  if (categoryQuantities) {
    const { platformBrandCode, categoryId } = category ?? {}
    if (platformBrandCode && categoryQuantities.has(platformBrandCode)) {
      return platformBrandCode
    }
    if (categoryId && categoryQuantities.has(categoryId)) {
      return categoryId
    }
  }
  return category?.platformBrandCode ?? category?.categoryId
}

/**
 * Groups cart entries by the product's category code and sums quantities.
 * Uses variantByCode to look up the category code for each cart entry's product code.
 *
 * @returns Map of categoryId → total quantity
 */
export const computeCategoryQuantities = (
  cartEntries: CartProductEntry[],
  variantByCode: Map<string, ProductDataVariant>
): Map<string, number> => {
  const quantities = new Map<string, number>()

  cartEntries.forEach((entry) => {
    const code = entry?.product?.code
    const variant = code ? variantByCode.get(code) : undefined
    const categoryId = getProductCategoryCode(variant)

    if (categoryId && entry.quantity) {
      const current = quantities.get(categoryId) ?? 0
      quantities.set(categoryId, current + entry.quantity)
    }
  })

  return quantities
}

/**
 * Returns category IDs where the total quantity meets or exceeds the category max.
 */
export const getCategoriesTouchingMax = (
  categoryQuantities: Map<string, number>,
  periodicityCategories: AutoDeliveryPeriodicityCategory[]
): string[] => {
  return periodicityCategories
    .filter((category) => {
      const key = getConfigCategoryKey(category, categoryQuantities)
      const { maxQty } = category
      if (!key || maxQty === undefined) {
        return false
      }
      const qty = categoryQuantities.get(key) ?? 0
      return qty >= maxQty
    })
    .map(
      (category) => getConfigCategoryKey(category, categoryQuantities) as string
    )
}

/**
 * Returns categories where the quantity is greater than 0 but below the category minimum.
 * Categories with 0 products in cart are NOT included.
 */
export const getCategoriesBelowMin = (
  categoryQuantities: Map<string, number>,
  periodicityCategories: AutoDeliveryPeriodicityCategory[]
): Array<{ categoryId: string; minQty: number }> => {
  return periodicityCategories
    .filter((category) => {
      const key = getConfigCategoryKey(category, categoryQuantities)
      const { minQty } = category
      if (!key || minQty === undefined) {
        return false
      }
      const qty = categoryQuantities.get(key) ?? 0
      return qty > 0 && qty < minQty
    })
    .map((category) => ({
      categoryId: getConfigCategoryKey(category, categoryQuantities) as string,
      minQty: category.minQty as number
    }))
}

/**
 * Resolves a human-readable category title from categoryNames config.
 * Falls back to the raw categoryId if no match is found.
 */
export const resolveCategoryTitle = (
  categoryId: string,
  categoryNames: CategoryNameMapping[]
): string => {
  const match = categoryNames?.find((c) => c.consumableFamily === categoryId)
  return match?.consumableTitleLabel ?? categoryId
}

/**
 * Replaces $category and $value markers in a template string.
 * Follows the same .replace() pattern used for $total_value in stickyBarTotalValueLabel.
 */
export const replaceCategoryMarkers = (
  template: string,
  categoryTitle: string,
  value?: number
): string => {
  let result = template.replace(/\$category/g, categoryTitle)
  if (value !== undefined) {
    result = result.replace(/\$value/g, String(value))
  }
  return result
}

/**
 * Returns true when productQuantity meets or exceeds maximumOrderQuantity.
 */
export const isProductAtMax = (
  productQuantity: number,
  maximumOrderQuantity?: number
): boolean => {
  if (maximumOrderQuantity === undefined) {
    return false
  }
  return productQuantity >= maximumOrderQuantity
}

/**
 * Computes the priority-based validation message for the Auto Delivery sticky bar.
 *
 * Priority order (returns first match):
 * 1. Cart max reached → maximumQuantityReached (continue enabled)
 * 2. Category max reached → maximumCategoryQuantityReached with $category (continue enabled)
 * 3. Category min not met → minimumCategoryQuantityRequired with $category/$value (continue disabled)
 * 4. Overall cart minimum not reached → minimumQuantityRequiredLabel (continue disabled)
 * 5. Empty cart OR not AD order type → minimumQuantityRequiredLabel (continue disabled)
 * 6. Otherwise → empty string (continue enabled)
 */
export const getValidationMessage = (
  params: ValidationMessageParams
): ValidationMessageResult => {
  const {
    stickyItemsCount,
    isAutoDeliveryCartOrderType,
    autoDeliveryMaxCart,
    autoDeliveryMinCart,
    autoDeliveryConfig,
    categoryQuantities,
    periodicityCategories,
    categoryNames
  } = params

  const disabledCategoryIds = getCategoriesTouchingMax(
    categoryQuantities,
    periodicityCategories
  ).slice()

  // Priority 1: Cart max reached
  if (stickyItemsCount >= autoDeliveryMaxCart && autoDeliveryMaxCart > 0) {
    return {
      message: autoDeliveryConfig.maximumQuantityReached ?? '',
      continueDisabled: false,
      disabledCategoryIds
    }
  }

  // Priority 2: Category max reached
  if (disabledCategoryIds.length > 0) {
    const commaSeparatedTitles = disabledCategoryIds
      .map((id) => resolveCategoryTitle(id, categoryNames))
      .join(', ')

    const message = replaceCategoryMarkers(
      autoDeliveryConfig.maximumCategoryQuantityReached ?? '',
      commaSeparatedTitles
    )

    return {
      message,
      continueDisabled: false,
      disabledCategoryIds
    }
  }

  // Priority 3: Category min not met
  const categoriesBelowMin = getCategoriesBelowMin(
    categoryQuantities,
    periodicityCategories
  )

  if (categoriesBelowMin.length > 0) {
    const firstCategory = categoriesBelowMin[0]
    const firstCategoryTitle = resolveCategoryTitle(
      firstCategory.categoryId,
      categoryNames
    )

    const message = replaceCategoryMarkers(
      autoDeliveryConfig.minimumCategoryQuantityRequired ?? '',
      firstCategoryTitle,
      firstCategory.minQty
    )

    return {
      message,
      continueDisabled: true,
      disabledCategoryIds
    }
  }

  // Priority 4: Overall cart minimum not reached (backward-compatible check — NFR-3)
  if (autoDeliveryMinCart && stickyItemsCount < autoDeliveryMinCart) {
    return {
      message: autoDeliveryConfig.minimumQuantityRequiredLabel ?? '',
      continueDisabled: true,
      disabledCategoryIds
    }
  }

  // Priority 5: Empty cart OR not AD order type
  if (stickyItemsCount === 0 || !isAutoDeliveryCartOrderType) {
    return {
      message: autoDeliveryConfig.minimumQuantityRequiredLabel ?? '',
      continueDisabled: true,
      disabledCategoryIds
    }
  }

  // Priority 6: All valid
  return {
    message: '',
    continueDisabled: false,
    disabledCategoryIds
  }
}
